import { useState, useEffect } from "react";
import { auth, db, storage, googleProvider } from "./firebase_config";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import {
  collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, setDoc, doc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const DOCS_OBLIGATOIRES = [
  { id: "avis_imposition", label: "Avis d'imposition N-1", obligatoire: true, desc: "Votre dernier avis d'imposition reçu" },
  { id: "bulletins_salaire", label: "Bulletin de salaire de décembre", obligatoire: true, desc: "Le bulletin de décembre récapitule toute l'année fiscale" },
  { id: "justif_identite", label: "Justificatif d'identité", obligatoire: true, desc: "Carte d'identité ou passeport en cours de validité" },
  { id: "rib", label: "RIB bancaire", obligatoire: true, desc: "Relevé d'identité bancaire pour le remboursement éventuel" },
];

const DOCS_COMPLEMENTAIRES = [
  { id: "revenus_fonciers", label: "Relevés de revenus fonciers", desc: "Si vous êtes propriétaire bailleur" },
  { id: "charges_deductibles", label: "Justificatifs de charges déductibles", desc: "Frais réels, pensions alimentaires..." },
  { id: "dons", label: "Reçus de dons et mécénat", desc: "Dons à des associations" },
  { id: "frais_garde", label: "Frais de garde d'enfants", desc: "Factures de crèche, assistante maternelle..." },
  { id: "travaux", label: "Factures de travaux", desc: "Travaux éligibles aux crédits d'impôt" },
  { id: "revenus_etrangers", label: "Revenus de source étrangère", desc: "Si vous avez des revenus à l'étranger" },
  { id: "plus_values", label: "Plus-values mobilières", desc: "Relevés de cession de titres" },
  { id: "pension_retraite", label: "Relevés de pension/retraite", desc: "Si vous êtes retraité" },
  { id: "chomage", label: "Attestation Pôle Emploi", desc: "Si vous avez perçu des allocations chômage" },
  { id: "autres", label: "Autres documents", desc: "Tout autre document que vous jugez utile" },
];

function DeclarationImpots({ user, onBack }) {
  const [uploads, setUploads] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploaded, setUploaded] = useState({});
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [uploadErrors, setUploadErrors] = useState({});

  const handleUpload = async (docId, file) => {
    if (!file) return;
    // Vérifier la taille (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadErrors(e => ({ ...e, [docId]: "Fichier trop volumineux (max 10MB)" }));
      return;
    }
    setUploading(u => ({ ...u, [docId]: true }));
    setUploadErrors(e => ({ ...e, [docId]: null }));
    try {
      const fileName = `${docId}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `declarations/${user.uid}/${new Date().getFullYear()}/${fileName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setUploaded(u => ({ ...u, [docId]: { nom: file.name, url } }));
    } catch (e) {
      console.error(e);
      setUploadErrors(err => ({ ...err, [docId]: "Erreur lors de l'envoi. Réessayez." }));
    }
    setUploading(u => ({ ...u, [docId]: false }));
  };

  const handleSubmit = async () => {
    setSending(true);
    try {
      await addDoc(collection(db, "declarations"), {
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || user.email,
        annee: new Date().getFullYear(),
        documents: uploaded,
        note,
        statut: "En attente",
        createdAt: serverTimestamp(),
      });
      // Notifier Daniela
      await notifierDaniela({
        sujet: "🧾 Nouveau dossier déclaration d'impôts",
        message: `Un client vient de déposer son dossier de déclaration d'impôts ${new Date().getFullYear()}. ${Object.keys(uploaded).length} document(s) joint(s).${note ? ` Message : "${note}"` : ""}`,
        clientEmail: user.email,
        clientNom: user.displayName || user.email,
      });
      setSent(true);
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const nbOblig = Object.keys(uploaded).filter(k => DOCS_OBLIGATOIRES.find(d => d.id === k)).length;
  const allObligDone = nbOblig === DOCS_OBLIGATOIRES.length;

  if (sent) return (
    <div style={{ textAlign: "center", padding: "48px 24px" }}>
      <div style={{ fontSize: 64, marginBottom: 20 }}>✅</div>
      <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: "#1A0A2E", marginBottom: 12 }}>Dossier envoyé !</h2>
      <p style={{ fontSize: 15, color: "#6B7280", marginBottom: 8 }}>Cordinel Daniela a bien reçu votre dossier de déclaration d'impôts.</p>
      <p style={{ fontSize: 14, color: "#9B7BB8", marginBottom: 24 }}>Elle vous contactera dans les 48h pour confirmer le rendez-vous.</p>
      <button onClick={onBack} style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", color: "#fff", border: "none", padding: "12px 28px", borderRadius: 50, fontFamily: "'DM Sans',sans-serif", fontWeight: 700, cursor: "pointer" }}>← Retour à mon espace</button>
    </div>
  );

  return (
    <div className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: "#f3e8ff", border: "none", color: "#6B2D8B", padding: "8px 16px", borderRadius: 20, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 13 }}>← Retour</button>
        <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: "#1A0A2E" }}>🧾 Déclaration d'impôts {new Date().getFullYear()}</h2>
      </div>

      {/* Progression */}
      <div style={{ background: "#fff", borderRadius: 16, padding: "20px 24px", marginBottom: 20, boxShadow: "0 2px 12px rgba(107,45,139,0.07)", border: "1.5px solid rgba(196,0,106,0.07)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#1A0A2E" }}>Progression du dossier</span>
          <span style={{ fontSize: 13, color: "#C4006A", fontWeight: 700 }}>{nbOblig}/{DOCS_OBLIGATOIRES.length} obligatoires</span>
        </div>
        <div style={{ background: "#f3e8ff", borderRadius: 50, height: 8, overflow: "hidden" }}>
          <div style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", height: "100%", width: `${(nbOblig / DOCS_OBLIGATOIRES.length) * 100}%`, transition: "width .3s", borderRadius: 50 }} />
        </div>
      </div>

      {/* Avertissement déplacement */}
      <div style={{ background: "linear-gradient(135deg,#1A0A2E,#6B2D8B)", borderRadius: 16, padding: "24px", marginBottom: 20, color: "#fff" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <span style={{ fontSize: 28 }}>💡</span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Pourquoi déposer vos documents en ligne ?</div>
            <p style={{ fontSize: 13, lineHeight: 1.8, color: "rgba(255,255,255,0.85)", marginBottom: 12 }}>
              CD'ASSIST CONSULTING n'a plus de bureau physique. Le dépôt en ligne est la méthode la plus simple, rapide et sécurisée.
            </p>
            <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px 16px", fontSize: 13, lineHeight: 1.7, color: "rgba(255,255,255,0.9)" }}>
              <strong>⚠️ Si vous préférez un rendez-vous physique :</strong><br/>
              Un déplacement à votre domicile ou lieu de travail sera facturé <strong style={{ color: "#FFD700" }}>25€</strong> en supplément.<br/>
              Prenez rendez-vous via la page <span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => window.location.href = "/"}>Contact</span> ou par WhatsApp au <strong>0690 96 10 04</strong>.
            </div>
          </div>
        </div>
      </div>
      <div style={{ background: "#fff", borderRadius: 16, padding: "24px", marginBottom: 20, boxShadow: "0 2px 12px rgba(107,45,139,0.07)", border: "1.5px solid rgba(196,0,106,0.07)" }}>
        <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: "#1A0A2E", marginBottom: 6 }}>📋 Documents obligatoires</h3>
        <p style={{ fontSize: 13, color: "#9B7BB8", marginBottom: 20 }}>Ces documents sont indispensables pour votre déclaration.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {DOCS_OBLIGATOIRES.map(doc => (
            <div key={doc.id} style={{ background: uploaded[doc.id] ? "rgba(16,185,129,0.05)" : "#faf7ff", borderRadius: 12, padding: "14px 18px", border: `1.5px solid ${uploaded[doc.id] ? "rgba(16,185,129,0.3)" : "rgba(196,0,106,0.1)"}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 16 }}>{uploaded[doc.id] ? "✅" : "📄"}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#1A0A2E" }}>{doc.label}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(196,0,106,0.1)", color: "#C4006A", padding: "2px 8px", borderRadius: 20 }}>OBLIGATOIRE</span>
                  </div>
                  <p style={{ fontSize: 12, color: "#9B7BB8", marginLeft: 24 }}>{doc.desc}</p>
                  {uploaded[doc.id] && <p style={{ fontSize: 12, color: "#059669", marginLeft: 24, marginTop: 4 }}>✓ {uploaded[doc.id].nom}</p>}
                  {uploadErrors[doc.id] && <p style={{ fontSize: 12, color: "#DC2626", marginLeft: 24, marginTop: 4 }}>❌ {uploadErrors[doc.id]}</p>}
                </div>
                <label style={{ cursor: "pointer", background: uploaded[doc.id] ? "rgba(107,45,139,0.1)" : "linear-gradient(135deg,#C4006A,#8B2FC9)", color: uploaded[doc.id] ? "#6B2D8B" : "#fff", border: uploaded[doc.id] ? "1.5px solid #6B2D8B" : "none", padding: "8px 16px", borderRadius: 50, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", fontFamily: "'DM Sans',sans-serif" }}>
                  {uploading[doc.id] ? "⏳ Envoi..." : uploaded[doc.id] ? "🔄 Remplacer" : "📎 Ajouter"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => { if(e.target.files[0]) handleUpload(doc.id, e.target.files[0]); e.target.value = ""; }} style={{ display: "none" }} />
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Documents complémentaires */}
      <div style={{ background: "#fff", borderRadius: 16, padding: "24px", marginBottom: 20, boxShadow: "0 2px 12px rgba(107,45,139,0.07)", border: "1.5px solid rgba(196,0,106,0.07)" }}>
        <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: "#1A0A2E", marginBottom: 6 }}>📎 Documents complémentaires</h3>
        <p style={{ fontSize: 13, color: "#9B7BB8", marginBottom: 20 }}>Ajoutez tout document que vous jugez utile pour votre déclaration.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {DOCS_COMPLEMENTAIRES.map(doc => (
            <div key={doc.id} style={{ background: uploaded[doc.id] ? "rgba(16,185,129,0.05)" : "#faf7ff", borderRadius: 12, padding: "12px 18px", border: `1.5px solid ${uploaded[doc.id] ? "rgba(16,185,129,0.3)" : "rgba(107,45,139,0.08)"}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 14 }}>{uploaded[doc.id] ? "✅" : "📄"}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1A0A2E" }}>{doc.label}</span>
                  </div>
                  <p style={{ fontSize: 12, color: "#9B7BB8", marginLeft: 22 }}>{doc.desc}</p>
                  {uploaded[doc.id] && <p style={{ fontSize: 12, color: "#059669", marginLeft: 22, marginTop: 2 }}>✓ {uploaded[doc.id].nom}</p>}
                </div>
                <label style={{ cursor: "pointer", background: uploaded[doc.id] ? "rgba(16,185,129,0.1)" : "#f3e8ff", color: uploaded[doc.id] ? "#059669" : "#6B2D8B", border: "none", padding: "7px 14px", borderRadius: 50, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", fontFamily: "'DM Sans',sans-serif" }}>
                  {uploading[doc.id] ? "⏳ Envoi..." : uploaded[doc.id] ? "🔄 Remplacer" : "+ Ajouter"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => { if(e.target.files[0]) handleUpload(doc.id, e.target.files[0]); e.target.value = ""; }} style={{ display: "none" }} />
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Note */}
      <div style={{ background: "#fff", borderRadius: 16, padding: "24px", marginBottom: 20, boxShadow: "0 2px 12px rgba(107,45,139,0.07)", border: "1.5px solid rgba(196,0,106,0.07)" }}>
        <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, color: "#1A0A2E", marginBottom: 12 }}>💬 Message pour Daniela</h3>
        <textarea className="client-input" rows={3} placeholder="Informations supplémentaires, questions, situation particulière..." value={note} onChange={e => setNote(e.target.value)} style={{ resize: "vertical" }} />
      </div>

      {/* Envoi */}
      {!allObligDone && (
        <div style={{ background: "rgba(245,158,11,0.1)", border: "1.5px solid rgba(245,158,11,0.3)", borderRadius: 12, padding: "14px 18px", marginBottom: 16, fontSize: 13, color: "#92400E" }}>
          ⚠️ Il manque {DOCS_OBLIGATOIRES.length - nbOblig} document(s) obligatoire(s) — vous pouvez quand même envoyer et les compléter plus tard.
        </div>
      )}

      <button onClick={handleSubmit} disabled={sending} style={{ width: "100%", background: allObligDone ? "linear-gradient(135deg,#C4006A,#8B2FC9)" : "linear-gradient(135deg,#9B7BB8,#6B2D8B)", color: "#fff", border: "none", padding: "16px", borderRadius: 50, fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 16, cursor: "pointer" }}>
        {sending ? "Envoi en cours..." : `📤 Envoyer mon dossier à Daniela (${Object.keys(uploaded).length} document${Object.keys(uploaded).length > 1 ? "s" : ""})`}
      </button>
      <p style={{ fontSize: 12, color: "#9B7BB8", textAlign: "center", marginTop: 10 }}>🔐 Vos documents sont chiffrés et sécurisés — conformité RGPD</p>
    </div>
  );
}

const NTFY_CHANNEL = "cdassist-notifications";

async function notifierDaniela({ sujet, message, clientEmail, clientNom }) {
  try {
    await fetch(`https://ntfy.sh/${NTFY_CHANNEL}`, {
      method: "POST",
      headers: {
        "Title": sujet.replace(/[^\x00-\x7F]/g, ""),
        "Priority": "high",
        "Content-Type": "text/plain; charset=utf-8",
      },
      body: `Client: ${clientNom}\nEmail: ${clientEmail}\n\n${message}`,
    });
  } catch (e) {
    console.log("Notification:", e);
  }
}

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'DM Sans',sans-serif;}
  .client-input{width:100%;border:1.5px solid #e5d5f5;border-radius:10px;padding:12px 16px;font-family:'DM Sans',sans-serif;font-size:14px;color:#1A0A2E;background:#faf7ff;outline:none;}
  .client-input:focus{border-color:#C4006A;box-shadow:0 0 0 3px rgba(196,0,106,0.09);}
  .client-btn{background:linear-gradient(135deg,#C4006A,#8B2FC9);color:#fff;border:none;padding:14px;border-radius:50px;font-family:'DM Sans',sans-serif;font-weight:700;font-size:14px;cursor:pointer;width:100%;transition:all .3s;}
  .client-btn:hover{transform:translateY(-2px);box-shadow:0 8px 28px rgba(196,0,106,0.4);}
  .nav-tab{padding:10px 20px;border-radius:50px;font-family:'DM Sans',sans-serif;font-weight:600;font-size:13px;cursor:pointer;border:none;transition:all .2s;}
  .nav-tab.active{background:linear-gradient(135deg,#C4006A,#8B2FC9);color:#fff;}
  .nav-tab.inactive{background:#f3e8ff;color:#6B2D8B;}
  .card{background:#fff;border-radius:16px;padding:24px;box-shadow:0 2px 16px rgba(107,45,139,0.07);border:1.5px solid rgba(196,0,106,0.07);}
  @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
  .fade-up{animation:fadeUp .5s ease both;}
  @keyframes pulse{0%,80%,100%{opacity:.2;transform:scale(.8)}40%{opacity:1;transform:scale(1.2)}}
`;

// ── Écran de connexion ──────────────────────────────────────────
function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ nom: "", email: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async () => {
    setError(""); setSuccess(""); setLoading(true);
    try {
      if (mode === "login") {
        const cred = await signInWithEmailAndPassword(auth, form.email, form.password);
        onAuth(cred.user);
      } else if (mode === "register") {
        if (form.password !== form.confirm) { setError("Les mots de passe ne correspondent pas."); setLoading(false); return; }
        const cred = await createUserWithEmailAndPassword(auth, form.email, form.password);
        await updateProfile(cred.user, { displayName: form.nom });
        onAuth(cred.user);
      } else if (mode === "forgot") {
        await sendPasswordResetEmail(auth, form.email);
        setSuccess("Email de réinitialisation envoyé ! Vérifiez votre boîte mail.");
      }
    } catch (e) {
      const msgs = {
        "auth/user-not-found": "Aucun compte trouvé avec cet email.",
        "auth/wrong-password": "Mot de passe incorrect.",
        "auth/email-already-in-use": "Cet email est déjà utilisé.",
        "auth/weak-password": "Le mot de passe doit contenir au moins 6 caractères.",
        "auth/invalid-email": "Email invalide.",
      };
      setError(msgs[e.code] || "Une erreur est survenue. Veuillez réessayer.");
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setError(""); setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      onAuth(result.user);
    } catch (e) {
      setError("Erreur de connexion Google. Veuillez réessayer.");
      setLoading(false);
    }
  };

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#faf7ff,#fff0f7)", padding: 24 }}>
      <style>{css}</style>
      <div style={{ background: "#fff", borderRadius: 24, padding: "40px 36px", maxWidth: 420, width: "100%", boxShadow: "0 24px 80px rgba(107,45,139,0.15)" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src="/LOGO_CD_ASSIST.png" alt="Logo" style={{ width: 80, marginBottom: 12 }} />
          <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: "#1A0A2E", marginBottom: 4 }}>
            {mode === "login" ? "Connexion" : mode === "register" ? "Créer un compte" : "Mot de passe oublié"}
          </h1>
          <p style={{ fontSize: 13, color: "#9B7BB8" }}>Espace client CD'ASSIST CONSULTING</p>
        </div>

        {error && <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#DC2626", padding: "10px 14px", borderRadius: 10, fontSize: 13, marginBottom: 16 }}>{error}</div>}
        {success && <div style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", color: "#059669", padding: "10px 14px", borderRadius: 10, fontSize: 13, marginBottom: 16 }}>{success}</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {mode === "register" && (
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#C4006A", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Nom complet</label>
              <input className="client-input" placeholder="Ex: Marie Dupont" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} />
            </div>
          )}
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#C4006A", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Email</label>
            <input className="client-input" type="email" placeholder="Ex: marie@exemple.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} onKeyDown={e => e.key === "Enter" && handleSubmit()} />
          </div>
          {mode !== "forgot" && (
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#C4006A", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Mot de passe</label>
              <input className="client-input" type="password" placeholder="••••••••" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} onKeyDown={e => e.key === "Enter" && handleSubmit()} />
            </div>
          )}
          {mode === "register" && (
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#C4006A", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Confirmer le mot de passe</label>
              <input className="client-input" type="password" placeholder="••••••••" value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} />
            </div>
          )}

          <button className="client-btn" onClick={handleSubmit} disabled={loading} style={{ marginTop: 8 }}>
            {loading ? "Chargement..." : mode === "login" ? "Se connecter" : mode === "register" ? "Créer mon compte" : "Envoyer le lien"}
          </button>

          {mode !== "forgot" && !isMobile && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0" }}>
                <div style={{ flex: 1, height: 1, background: "#f3e8ff" }} />
                <span style={{ fontSize: 12, color: "#9B7BB8" }}>ou</span>
                <div style={{ flex: 1, height: 1, background: "#f3e8ff" }} />
              </div>
              <button onClick={handleGoogle} disabled={loading} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "13px", border: "1.5px solid #e5d5f5", borderRadius: 50, background: "#fff", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 14, color: "#374151" }}>
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Continuer avec Google
              </button>
            </>
          )}
        </div>

        <div style={{ marginTop: 20, textAlign: "center", display: "flex", flexDirection: "column", gap: 8 }}>
          {mode === "login" && <>
            <button onClick={() => setMode("register")} style={{ background: "none", border: "none", color: "#C4006A", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>Pas encore de compte ? S'inscrire</button>
            <button onClick={() => setMode("forgot")} style={{ background: "none", border: "none", color: "#9B7BB8", fontSize: 12, cursor: "pointer" }}>Mot de passe oublié ?</button>
          </>}
          {mode === "register" && <button onClick={() => setMode("login")} style={{ background: "none", border: "none", color: "#C4006A", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>Déjà un compte ? Se connecter</button>}
          {mode === "forgot" && <button onClick={() => setMode("login")} style={{ background: "none", border: "none", color: "#C4006A", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>← Retour à la connexion</button>}
        </div>
      </div>
    </div>
  );
}

function BoutiqueClient({ C1, C2 }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(query(collection(db, "articles_boutique"), where("actif", "==", true), orderBy("createdAt", "desc")));
        setArticles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.log(e); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: "48px", color: "#9B7BB8" }}>Chargement...</div>;

  return (
    <div className="fade-up">
      <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: "#1A0A2E", marginBottom: 8 }}>🎁 Outils & Modèles</h2>
      <p style={{ fontSize: 14, color: "#9B7BB8", marginBottom: 28 }}>Documents professionnels prêts à l'emploi.</p>

      {articles.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 16, padding: "48px", textAlign: "center", boxShadow: "0 2px 12px rgba(107,45,139,0.06)" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎁</div>
          <p style={{ color: "#9B7BB8" }}>Aucun outil disponible pour le moment.</p>
          <p style={{ fontSize: 13, color: "#9B7BB8", marginTop: 8 }}>Revenez bientôt !</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 20 }}>
          {articles.map(a => (
            <div key={a.id} style={{ background: "#fff", borderRadius: 16, padding: "24px", boxShadow: "0 2px 12px rgba(107,45,139,0.07)", border: "1.5px solid rgba(196,0,106,0.07)", display: "flex", flexDirection: "column" }}>
              <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(196,0,106,0.1)", color: C1, padding: "3px 10px", borderRadius: 20, alignSelf: "flex-start", marginBottom: 12 }}>{a.categorie}</span>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, color: "#1A0A2E", marginBottom: 8 }}>{a.nom}</h3>
              <p style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6, flex: 1, marginBottom: 16 }}>{a.description}</p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 700, color: C1 }}>{a.prix}€</span>
                <span style={{ fontSize: 11, background: "rgba(16,185,129,0.1)", color: "#059669", padding: "4px 10px", borderRadius: 20, fontWeight: 600 }}>📄 PDF</span>
              </div>
              {a.lienPaiement ? (
                <a href={a.lienPaiement} target="_blank" rel="noopener noreferrer"
                  style={{ display: "block", background: `linear-gradient(135deg,${C1},${C2})`, color: "#fff", textDecoration: "none", padding: "12px", borderRadius: 50, textAlign: "center", fontSize: 14, fontWeight: 700 }}>
                  💳 Acheter & Télécharger
                </a>
              ) : (
                <a href="mailto:daniela.cordinel@cd-assist.com"
                  style={{ display: "block", background: `linear-gradient(135deg,${C1},${C2})`, color: "#fff", textDecoration: "none", padding: "12px", borderRadius: 50, textAlign: "center", fontSize: 14, fontWeight: 700 }}>
                  📩 Contacter pour acheter
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Espace client ───────────────────────────────────────────────
function ClientDashboard({ user, onLogout }) {
  const [tab, setTab] = useState("accueil");
  const [showDeclaration, setShowDeclaration] = useState(false);
  const [declarationStatut, setDeclarationStatut] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [fichiers, setFichiers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [rdvs, setRdvs] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msgSent, setMsgSent] = useState(false);

  useEffect(() => {
    if (user) {
      loadData();
      saveProfile();
    }
  }, [user]);

  useEffect(() => {
    if (user && tab === "declaration") {
      const loadStatut = async () => {
        try {
          const declQ = query(collection(db, "declarations"), where("userId", "==", user.uid), orderBy("createdAt", "desc"));
          const declSnap = await getDocs(declQ);
          if (!declSnap.empty) setDeclarationStatut(declSnap.docs[0].data().statut || "En attente");
        } catch (e) { console.log(e); }
      };
      loadStatut();
    }
  }, [tab, user]);

  const saveProfile = async () => {
    if (!user) return;
    try {
      const isNew = (await getDocs(query(collection(db, "clients"), where("userId", "==", user.uid)))).empty;
      await setDoc(doc(db, "clients", user.uid), {
        userId: user.uid,
        email: user.email,
        nom: user.displayName || user.email,
        derniereConnexion: serverTimestamp(),
      }, { merge: true });
      if (isNew) {
        await notifierDaniela({
          sujet: "👤 Nouveau client inscrit !",
          message: `Un nouveau client vient de créer son compte.`,
          clientEmail: user.email,
          clientNom: user.displayName || user.email,
        });
      }
    } catch (e) { console.log("Profile:", e); }
  };

  const loadData = async () => {
    if (!user) return;
    try {
      // Messages
      const msgQ = query(collection(db, "messages"), where("userId", "==", user.uid), orderBy("createdAt", "asc"));
      const msgSnap = await getDocs(msgQ);
      setMessages(msgSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Fichiers
      const fichQ = query(collection(db, "fichiers"), where("userId", "==", user.uid), orderBy("createdAt", "desc"));
      const fichSnap = await getDocs(fichQ);
      setFichiers(fichSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Documents
      const docsQ = query(collection(db, "documents"), where("userId", "==", user.uid), orderBy("createdAt", "desc"));
      const docsSnap = await getDocs(docsQ);
      setDocuments(docsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Commandes
      const cmdQ = query(collection(db, "commandes"), where("userId", "==", user.uid), orderBy("createdAt", "desc"));
      const cmdSnap = await getDocs(cmdQ);
      setCommandes(cmdSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // RDVs
      try {
        const rdvQ = query(collection(db, "rdvs"), where("email", "==", user.email), orderBy("createdAt", "desc"));
        const rdvSnap = await getDocs(rdvQ);
        setRdvs(rdvSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.log("RDVs:", e); }

      // Statut déclaration
      try {
        const declQ = query(collection(db, "declarations"), where("userId", "==", user.uid), orderBy("createdAt", "desc"));
        const declSnap = await getDocs(declQ);
        if (!declSnap.empty) setDeclarationStatut(declSnap.docs[0].data().statut || "En attente");
      } catch (e) { console.log("Decl:", e); }

    } catch (e) {
      console.error("Erreur chargement données:", e);
    }
  };

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    await addDoc(collection(db, "messages"), {
      userId: user.uid,
      userEmail: user.email,
      userName: user.displayName || user.email,
      content: newMsg,
      from: "client",
      createdAt: serverTimestamp(),
    });
    // Notifier Daniela
    await notifierDaniela({
      sujet: "💬 Nouveau message client",
      message: newMsg,
      clientEmail: user.email,
      clientNom: user.displayName || user.email,
    });
    setNewMsg("");
    setMsgSent(true);
    setTimeout(() => setMsgSent(false), 3000);
    loadData();
  };

  const deleteFichier = async (fichier) => {
    if (!window.confirm(`Supprimer "${fichier.nom}" ?`)) return;
    try {
      // Supprimer depuis Storage via l'URL
      const storageRef = ref(storage, fichier.url);
      try { await deleteObject(storageRef); } catch (e) { console.log("Storage:", e); }
      // Supprimer depuis Firestore
      const { deleteDoc, doc: firestoreDoc } = await import("firebase/firestore");
      await deleteDoc(firestoreDoc(db, "fichiers", fichier.id));
      loadData();
    } catch (e) { console.error(e); }
  };

  const uploadFichier = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const storageRef = ref(storage, `clients/${user.uid}/${uploadFile.name}`);
      await uploadBytes(storageRef, uploadFile);
      const url = await getDownloadURL(storageRef);
      await addDoc(collection(db, "fichiers"), {
        userId: user.uid,
        userEmail: user.email,
        nom: uploadFile.name,
        url,
        taille: `${(uploadFile.size / 1024).toFixed(0)} Ko`,
        createdAt: serverTimestamp(),
      });
      setUploadFile(null);
      loadData();
    } catch (e) { console.error(e); }
    setUploading(false);
  };

  const TABS = [
    { id: "accueil", icon: "🏠", label: "Accueil" },
    { id: "declaration", icon: "🧾", label: "Ma déclaration" },
    { id: "boutique", icon: "🎁", label: "Outils & Modèles" },
    { id: "documents", icon: "📄", label: "Mes documents" },
    { id: "commandes", icon: "🛒", label: "Mes commandes" },
    { id: "fichiers", icon: "📁", label: "Mes fichiers" },
    { id: "messages", icon: "💬", label: "Messagerie" },
    { id: "rdvs", icon: "📅", label: "Mes RDV" },
  ];

  const S = {
    card: { background: "#fff", borderRadius: 16, padding: "24px", boxShadow: "0 2px 16px rgba(107,45,139,0.07)", border: "1.5px solid rgba(196,0,106,0.07)" },
  };

  return (
    <div style={{ minHeight: "100vh", background: "#faf7ff", fontFamily: "'DM Sans',sans-serif" }}>
      <style>{css}</style>

      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #f3e8ff", padding: "16px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src="/LOGO_CD_ASSIST.png" alt="Logo" style={{ width: 44 }} />
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 16, fontWeight: 700, color: "#6B2D8B" }}>CD'ASSIST</div>
            <div style={{ fontSize: 10, color: "#C4006A", letterSpacing: 2, textTransform: "uppercase" }}>Espace client</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1A0A2E" }}>{user.displayName || user.email}</div>
            <div style={{ fontSize: 12, color: "#9B7BB8" }}>{user.email}</div>
          </div>
          <button onClick={onLogout} style={{ background: "rgba(239,68,68,0.1)", color: "#DC2626", border: "none", padding: "8px 16px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            Déconnexion
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "32px 24px" }}>
        {/* Navigation tabs */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 32 }}>
          {TABS.map(t => (
            <button key={t.id} className={`nav-tab ${tab === t.id ? "active" : "inactive"}`} onClick={() => { setTab(t.id); setShowDeclaration(false); }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* MA DÉCLARATION */}
        {tab === "declaration" && !showDeclaration && (
          <div className="fade-up">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: "#1A0A2E" }}>🧾 Ma déclaration d'impôts</h2>
              <button onClick={async () => {
                try {
                  const declQ = query(collection(db, "declarations"), where("userId", "==", user.uid), orderBy("createdAt", "desc"));
                  const declSnap = await getDocs(declQ);
                  if (!declSnap.empty) setDeclarationStatut(declSnap.docs[0].data().statut || "En attente");
                } catch (e) { console.log(e); }
              }} style={{ background: "#f3e8ff", color: "#6B2D8B", border: "none", padding: "9px 18px", borderRadius: 50, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                🔄 Actualiser
              </button>
            </div>
            <p style={{ fontSize: 14, color: "#9B7BB8", marginBottom: 24 }}>Suivez l'avancement de votre dossier fiscal.</p>

            {/* Étapes de suivi */}
            <div style={{ ...S.card, marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, color: "#1A0A2E", marginBottom: 20 }}>📊 Statut de votre dossier</h3>
              {[
                { id: "En attente", label: "En attente", desc: "Votre dossier est en attente de traitement", emoji: "⏳" },
                { id: "Dossier réceptionné", label: "Dossier réceptionné", desc: "Daniela a bien reçu vos documents", emoji: "📬" },
                { id: "En cours de traitement", label: "En cours de traitement", desc: "Votre déclaration est en cours de préparation", emoji: "🔄" },
                { id: "Traité", label: "Traité", desc: "Votre déclaration a été traitée et soumise", emoji: "✅" },
              ].map((etape, i) => {
                const statutOrder = ["En attente", "Dossier réceptionné", "En cours de traitement", "Traité"];
                const currentIndex = statutOrder.indexOf(declarationStatut || "En attente");
                const etapeIndex = statutOrder.indexOf(etape.id);
                const isActive = etapeIndex === currentIndex;
                const isDone = etapeIndex < currentIndex;

                return (
                  <div key={etape.id} style={{ display: "flex", gap: 16, marginBottom: 16, alignItems: "flex-start" }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0,
                      background: isDone ? "linear-gradient(135deg,#059669,#34D399)" : isActive ? "linear-gradient(135deg,#C4006A,#8B2FC9)" : "#f3e8ff",
                      color: isDone || isActive ? "#fff" : "#9B7BB8" }}>
                      {isDone ? "✓" : etape.emoji}
                    </div>
                    <div style={{ flex: 1, paddingTop: 4 }}>
                      <div style={{ fontSize: 14, fontWeight: isActive ? 700 : 600, color: isActive ? "#C4006A" : isDone ? "#059669" : "#9B7BB8" }}>{etape.label}</div>
                      <div style={{ fontSize: 12, color: "#9B7BB8", marginTop: 2 }}>{etape.desc}</div>
                    </div>
                    {isActive && <span style={{ fontSize: 11, fontWeight: 700, background: "rgba(196,0,106,0.1)", color: "#C4006A", padding: "3px 10px", borderRadius: 20 }}>En cours</span>}
                  </div>
                );
              })}
            </div>

            {declarationStatut ? (
              <div style={{ ...S.card, background: "rgba(16,185,129,0.05)", border: "1.5px solid rgba(16,185,129,0.2)", marginBottom: 20 }}>
                <p style={{ fontSize: 14, color: "#374151" }}>📅 Votre dossier a été déposé pour l'année <strong>{new Date().getFullYear()}</strong>.</p>
                <p style={{ fontSize: 13, color: "#6B7280", marginTop: 6 }}>Pour toute question, contactez Daniela via la messagerie.</p>
              </div>
            ) : (
              <div style={{ ...S.card, background: "rgba(245,158,11,0.05)", border: "1.5px solid rgba(245,158,11,0.2)", marginBottom: 20 }}>
                <p style={{ fontSize: 14, color: "#374151" }}>⚠️ Vous n'avez pas encore déposé votre dossier de déclaration d'impôts.</p>
              </div>
            )}

            <button onClick={() => setShowDeclaration(true)} style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", color: "#fff", border: "none", padding: "14px 28px", borderRadius: 50, fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
              📤 {declarationStatut ? "Compléter mon dossier" : "Déposer mes documents"} →
            </button>
          </div>
        )}

        {/* Page Déclaration d'impôts */}
        {showDeclaration && (
          <DeclarationImpots user={user} onBack={() => setShowDeclaration(false)} />
        )}

        {/* ACCUEIL */}
        {tab === "accueil" && !showDeclaration && (
          <div className="fade-up">
            <div style={{ ...S.card, background: "linear-gradient(135deg,#6B2D8B,#C4006A)", color: "#fff", marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, marginBottom: 8 }}>
                  Bonjour {user.displayName?.split(" ")[0] || "!"} 👋
                </h1>
                <p style={{ fontSize: 14, opacity: 0.85 }}>Bienvenue dans votre espace client CD'ASSIST CONSULTING</p>
              </div>
              <button onClick={() => { loadData(); window.location.reload(); }} style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.4)", padding: "9px 18px", borderRadius: 50, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                🔄 Actualiser
              </button>
            </div>

            {/* Déclaration d'impôts — EN HAUT */}
            <div style={{ ...S.card, marginBottom: 16, background: "linear-gradient(135deg,rgba(196,0,106,0.05),rgba(107,45,139,0.05))", border: "1.5px solid rgba(196,0,106,0.2)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <span style={{ fontSize: 36 }}>🧾</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1A0A2E", marginBottom: 4 }}>Déclaration d'impôts {new Date().getFullYear()}</div>
                  <div style={{ fontSize: 13, color: "#6B7280" }}>Déposez vos documents fiscaux en toute sécurité pour votre déclaration annuelle.</div>
                  {/* Statut du dossier */}
                  {declarationStatut && (
                    <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                      background: declarationStatut === "Traité" ? "rgba(16,185,129,0.1)" :
                        declarationStatut === "En cours de traitement" ? "rgba(59,130,246,0.1)" :
                        declarationStatut === "Dossier réceptionné" ? "rgba(107,45,139,0.1)" :
                        "rgba(245,158,11,0.1)",
                      color: declarationStatut === "Traité" ? "#059669" :
                        declarationStatut === "En cours de traitement" ? "#2563EB" :
                        declarationStatut === "Dossier réceptionné" ? "#6B2D8B" :
                        "#D97706",
                    }}>
                      {declarationStatut === "Traité" ? "✅" :
                        declarationStatut === "En cours de traitement" ? "🔄" :
                        declarationStatut === "Dossier réceptionné" ? "📬" : "⏳"}
                      {declarationStatut}
                    </div>
                  )}
                </div>
                <button onClick={() => { setShowDeclaration(true); setTab(""); }} style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", color: "#fff", border: "none", padding: "12px 22px", borderRadius: 50, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  📤 Déposer mes documents →
                </button>
              </div>
            </div>

            {/* Boutique — EN HAUT */}
            <div style={{ ...S.card, marginBottom: 24, background: "linear-gradient(135deg,rgba(139,47,201,0.05),rgba(196,0,106,0.05))", border: "1.5px solid rgba(139,47,201,0.2)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <span style={{ fontSize: 36 }}>🎁</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1A0A2E", marginBottom: 4 }}>Outils & Modèles</div>
                  <div style={{ fontSize: 13, color: "#6B7280" }}>Documents professionnels prêts à l'emploi — contrats, guides, templates...</div>
                </div>
                <button onClick={() => { setShowDeclaration(false); setTab("boutique"); }} style={{ background: "linear-gradient(135deg,#8B2FC9,#C4006A)", color: "#fff", border: "none", padding: "12px 22px", borderRadius: 50, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  🎁 Découvrir →
                </button>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 16 }}>
              {[
                { icon: "📄", label: "Documents", count: documents.length, tab: "documents" },
                { icon: "🛒", label: "Commandes", count: commandes.length, tab: "commandes" },
                { icon: "📁", label: "Fichiers", count: fichiers.length, tab: "fichiers" },
                { icon: "💬", label: "Messages", count: messages.length, tab: "messages" },
                { icon: "📅", label: "RDV", count: rdvs.length, tab: "rdvs" },
              ].map(s => (
                <div key={s.tab} onClick={() => setTab(s.tab)} style={{ ...S.card, cursor: "pointer", textAlign: "center", transition: "all .3s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.borderColor = "#C4006A"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.borderColor = "rgba(196,0,106,0.07)"; }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>{s.icon}</div>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 700, color: "#C4006A" }}>{s.count}</div>
                  <div style={{ fontSize: 13, color: "#9B7BB8" }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ ...S.card, marginTop: 24, display: "flex", alignItems: "center", gap: 16 }}>
              <span style={{ fontSize: 28 }}>💬</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#1A0A2E", marginBottom: 4 }}>Besoin d'aide ?</div>
                <div style={{ fontSize: 13, color: "#9B7BB8" }}>Contactez Cordinel Daniela directement via la messagerie</div>
              </div>
              <button onClick={() => setTab("messages")} style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 50, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Écrire →</button>
            </div>
          </div>
        )}

        {/* DOCUMENTS */}
        {/* BOUTIQUE */}
        {tab === "boutique" && (
          <BoutiqueClient C1="#C4006A" C2="#6B2D8B" />
        )}

        {tab === "documents" && (
          <div className="fade-up">
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: "#1A0A2E", marginBottom: 24 }}>📄 Mes documents générés</h2>
            {documents.length === 0 ? (
              <div style={{ ...S.card, textAlign: "center", padding: "48px" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
                <p style={{ fontSize: 15, color: "#9B7BB8", marginBottom: 20 }}>Vous n'avez pas encore de documents générés.</p>
                <button onClick={() => window.location.href = "/"} style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", color: "#fff", border: "none", padding: "12px 28px", borderRadius: 50, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Générer un document →
                </button>
              </div>
            ) : documents.map(d => (
              <div key={d.id} style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1A0A2E" }}>{d.type}</div>
                  <div style={{ fontSize: 12, color: "#9B7BB8", marginTop: 4 }}>{d.createdAt?.toDate?.().toLocaleDateString("fr-FR")}</div>
                </div>
                <button style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", color: "#fff", border: "none", padding: "8px 18px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>⬇ Télécharger</button>
              </div>
            ))}
          </div>
        )}

        {/* COMMANDES */}
        {tab === "commandes" && (
          <div className="fade-up">
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: "#1A0A2E", marginBottom: 24 }}>🛒 Mes commandes</h2>
            {commandes.length === 0 ? (
              <div style={{ ...S.card, textAlign: "center", padding: "48px" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🛒</div>
                <p style={{ fontSize: 15, color: "#9B7BB8", marginBottom: 20 }}>Vous n'avez pas encore de commandes.</p>
                <button onClick={() => window.location.href = "/"} style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", color: "#fff", border: "none", padding: "12px 28px", borderRadius: 50, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Découvrir nos services →
                </button>
              </div>
            ) : commandes.map(c => (
              <div key={c.id} style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1A0A2E" }}>{c.service}</div>
                  <div style={{ fontSize: 12, color: "#9B7BB8", marginTop: 4 }}>{c.createdAt?.toDate?.().toLocaleDateString("fr-FR")} · {c.montant}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20, background: c.statut === "payé" ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)", color: c.statut === "payé" ? "#059669" : "#D97706" }}>{c.statut || "En cours"}</span>
              </div>
            ))}
          </div>
        )}

        {/* FICHIERS */}
        {tab === "fichiers" && (
          <div className="fade-up">
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: "#1A0A2E", marginBottom: 24 }}>📁 Mes fichiers</h2>
            <div style={{ ...S.card, marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, color: "#1A0A2E", marginBottom: 16 }}>Envoyer un fichier</h3>
              <label style={{ display: "block", border: "2px dashed rgba(196,0,106,0.3)", borderRadius: 12, padding: "24px", textAlign: "center", cursor: "pointer", background: "rgba(196,0,106,0.02)", marginBottom: 12 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📎</div>
                <div style={{ fontSize: 14, color: "#C4006A", fontWeight: 600 }}>{uploadFile ? uploadFile.name : "Cliquer pour sélectionner un fichier"}</div>
                <div style={{ fontSize: 12, color: "#9B7BB8", marginTop: 4 }}>PDF, JPG, PNG, DOC acceptés</div>
                <input type="file" onChange={e => setUploadFile(e.target.files[0])} style={{ display: "none" }} />
              </label>
              {uploadFile && (
                <button onClick={uploadFichier} disabled={uploading} style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", color: "#fff", border: "none", padding: "12px 28px", borderRadius: 50, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  {uploading ? "Envoi en cours..." : "⬆ Envoyer le fichier"}
                </button>
              )}
            </div>
            {fichiers.length === 0 ? (
              <div style={{ ...S.card, textAlign: "center", padding: "32px" }}>
                <p style={{ fontSize: 14, color: "#9B7BB8" }}>Aucun fichier envoyé pour le moment.</p>
              </div>
            ) : fichiers.map(f => (
              <div key={f.id} style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 24 }}>📄</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#1A0A2E" }}>{f.nom}</div>
                    <div style={{ fontSize: 12, color: "#9B7BB8" }}>{f.taille} · {f.createdAt?.toDate?.().toLocaleDateString("fr-FR")}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <a href={f.url} download={f.nom} target="_blank" rel="noopener noreferrer" style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", color: "#fff", textDecoration: "none", padding: "8px 18px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>⬇ Télécharger</a>
                  <button onClick={() => deleteFichier(f)} style={{ background: "rgba(239,68,68,0.1)", color: "#DC2626", border: "none", padding: "8px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* MESSAGERIE */}
        {tab === "messages" && (
          <div className="fade-up">
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: "#1A0A2E", marginBottom: 24 }}>💬 Messagerie</h2>
            <div style={{ ...S.card, marginBottom: 16, minHeight: 300, maxHeight: 400, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
              {messages.length === 0 ? (
                <div style={{ textAlign: "center", padding: "48px 0", color: "#9B7BB8", fontSize: 14 }}>
                  Envoyez votre premier message à Cordinel Daniela !
                </div>
              ) : messages.map(m => (
                <div key={m.id} style={{ display: "flex", justifyContent: m.from === "client" ? "flex-end" : "flex-start" }}>
                  <div style={{ maxWidth: "70%", padding: "10px 16px", borderRadius: m.from === "client" ? "16px 16px 4px 16px" : "16px 16px 16px 4px", background: m.from === "client" ? "linear-gradient(135deg,#C4006A,#8B2FC9)" : "#f3e8ff", color: m.from === "client" ? "#fff" : "#1A0A2E", fontSize: 14, lineHeight: 1.5 }}>
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
            {msgSent && <div style={{ background: "rgba(16,185,129,0.1)", color: "#059669", padding: "10px 14px", borderRadius: 10, fontSize: 13, marginBottom: 12, fontWeight: 600 }}>✅ Message envoyé ! Cordinel Daniela vous répondra dans les meilleurs délais.</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <input className="client-input" placeholder="Votre message..." value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()} style={{ flex: 1 }} />
              <button onClick={sendMessage} style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", color: "#fff", border: "none", padding: "12px 24px", borderRadius: 50, fontSize: 14, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Envoyer →</button>
            </div>
          </div>
        )}

        {/* RDVs */}
        {tab === "rdvs" && (
          <div className="fade-up">
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, color: "#1A0A2E", marginBottom: 24 }}>📅 Mes rendez-vous</h2>
            {rdvs.length === 0 ? (
              <div style={{ ...S.card, textAlign: "center", padding: "48px" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📅</div>
                <p style={{ fontSize: 15, color: "#9B7BB8", marginBottom: 20 }}>Aucun rendez-vous planifié.</p>
                <button onClick={() => window.location.href = "/"} style={{ background: "linear-gradient(135deg,#C4006A,#8B2FC9)", color: "#fff", border: "none", padding: "12px 28px", borderRadius: 50, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Prendre un RDV →
                </button>
              </div>
            ) : rdvs.map(r => (
              <div key={r.id} style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1A0A2E" }}>{r.service}</div>
                  <div style={{ fontSize: 12, color: "#9B7BB8", marginTop: 4 }}>📅 {r.date} à {r.heure}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20, background: r.statut === "confirmé" ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)", color: r.statut === "confirmé" ? "#059669" : "#D97706" }}>{r.statut || "En attente"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Composant principal ─────────────────────────────────────────
export default function EspaceClient() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          setUser(result.user);
          setLoading(false);
          return;
        }
      } catch (e) {
        console.log("Redirect:", e.code);
      }
      const unsub = onAuthStateChanged(auth, (u) => {
        setUser(u);
        setLoading(false);
      });
      return unsub;
    };
    const cleanup = init();
    return () => cleanup.then(unsub => unsub && unsub());
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#faf7ff" }}>
      <div style={{ display: "flex", gap: 8 }}>
        {[0,1,2].map(i => <div key={i} style={{ width: 12, height: 12, borderRadius: "50%", background: "#C4006A", animation: `pulse 1.2s ${i*0.2}s infinite` }} />)}
      </div>
    </div>
  );

  if (!user) return <AuthScreen onAuth={setUser} />;
  return <ClientDashboard user={user} onLogout={handleLogout} />;
}
